import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import NotificationsIcon from "@mui/icons-material/Notifications";
import ChevronRight from "@mui/icons-material/ChevronRight";
import {
  Avatar,
  Box,
  Menu,
  Typography,
  IconButton,
  Badge as MuiBadge,
} from "@mui/material";
import {
  useInfiniteNotifications,
  useMarkNotificationAsRead,
  useUnreadCount,
} from "@/hooks/useNotification";
import { useNotificationStore } from "@/store/notification.store";
import { useDashboardTheme } from "../MeetingDashboard/themeHelper";

const NotificationPopover = () => {
  const { isDark, colors } = useDashboardTheme();
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } =
    useInfiniteNotifications(true);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const navigate = useNavigate();

  const { data: unreadCount } = useUnreadCount();
  const setUnreadCount = useNotificationStore((state) => state.setUnreadCount);
  const { mutate: markNotificationAsRead } = useMarkNotificationAsRead();

  const observerRef = useRef<IntersectionObserver | null>(null);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (unreadCount !== undefined) {
      setUnreadCount(unreadCount);
    }
  }, [unreadCount, setUnreadCount]);

  const notifications = data?.pages.flatMap((page) => page.items) ?? [];

  function getInitials(name: string): string {
    if (!name) return "U";
    const parts = name.split(" ");
    if (parts.length === 1) return parts[0][0].toUpperCase();
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }

  useEffect(() => {
    if (!loadMoreRef.current || !hasNextPage) return;

    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      {
        root: document.querySelector(".MuiPopover-paper"),
        threshold: 1.0,
      },
    );

    observerRef.current.observe(loadMoreRef.current);

    return () => observerRef.current?.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const handleViewMore = () => {
    navigate("/notification");
    setAnchorEl(null);
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <IconButton color="inherit" onClick={handleClick}>
        <MuiBadge
          badgeContent={unreadCount}
          color="warning"
          overlap="circular"
          invisible={!unreadCount}
        >
          <NotificationsIcon />
        </MuiBadge>
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            bgcolor: isDark ? "#1C1A26" : "#FFFFFF",
            backdropFilter: "blur(20px)",
            borderRadius: 3,
            width: 380,
            p: 1.5,
            maxHeight: 500,
            boxShadow: isDark
              ? "0 10px 40px rgba(0, 0, 0, 0.6)"
              : "0 10px 40px rgba(0, 0, 0, 0.12)",
            border: `1px solid ${colors.border}`,
            color: colors.text,
          },
        }}
      >
        {!isLoading && notifications.length === 0 && (
          <Box p={2}>
            <Typography variant="body2" sx={{ color: colors.textMuted }}>
              No notifications
            </Typography>
          </Box>
        )}

        {notifications.map((n, index) => (
          <Box
            key={n!.id}
            onClick={() => {
              if (!n!.read) {
                markNotificationAsRead(n!.id);
              }
              setAnchorEl(null);
            }}
            ref={index === 3 ? loadMoreRef : null}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              p: 1.5,
              mb: 1,
              borderRadius: 2,
              backgroundColor: isDark ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.03)",
              border: `1px solid ${colors.border}`,
              cursor: "pointer",
              transition: "all 0.2s",
              "&:hover": {
                backgroundColor: isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.06)",
              },
            }}
          >
            <Avatar
              sx={{
                bgcolor: isDark ? "rgba(255, 255, 255, 0.15)" : "rgba(0, 0, 0, 0.08)",
                color: colors.text,
                width: 36,
                height: 36,
                fontSize: 13,
                fontWeight: "bold",
                border: `1px solid ${colors.border}`,
              }}
            >
              {getInitials(n!.User?.name || "U")}
            </Avatar>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="subtitle2"
                sx={{ color: colors.text, fontWeight: 600 }}
                noWrap
              >
                {n!.User?.name || "Unknown"}
              </Typography>
              <Typography variant="body2" sx={{ color: colors.textMuted }} noWrap>
                {n!.message}
              </Typography>
            </Box>

            <ChevronRight sx={{ color: colors.textMuted, fontSize: 18 }} />
          </Box>
        ))}

        {isFetchingNextPage && (
          <Box p={1.5}>
            <Typography variant="body2" sx={{ color: colors.textMuted }} textAlign="center">
              Loading more...
            </Typography>
          </Box>
        )}

        <Box
          onClick={handleViewMore}
          sx={{
            p: 1.5,
            borderRadius: 2,
            textAlign: "center",
            cursor: "pointer",
            transition: "background 0.3s",
            backgroundColor: isDark ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.03)",
            border: `1px solid ${colors.border}`,
            "&:hover": {
              backgroundColor: isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.06)",
            },
          }}
        >
          <Typography variant="body2" sx={{ color: isDark ? "#B8F890" : "#5a9d50", fontWeight: "bold" }}>
            View all notifications
          </Typography>
        </Box>
      </Menu>
    </>
  );
};

export default NotificationPopover;
